import type { BlobLike } from "@pdfking/compatibility"
import { FileCache } from "@pdfking/compatibility"
import type { AsyncSequence, Bytes, Time } from "@pdfking/tfunc"
import {
  FilterAsync,
  First,
  FullMethodDispatcher,
  Items,
  Peek,
  SafePromise,
  UnixTime,
  Void,
} from "@pdfking/tfunc"

import { AutoThrow } from "../errors/AutoThrow"
import { EngineError } from "../errors/EngineError"
import { FileContentGUID } from "../methods/FileContentGUID"
import { FileContentSize } from "../methods/FileContentSize"
import type { PdfEngine } from "../types/engine/PdfEngine"
import { ErrorName } from "../types/errors/ErrorName"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { RemoteFile } from "../types/files/cached/RemoteFile"
import type { VirtualFile } from "../types/files/VirtualFile"
import { VirtualFileType } from "../types/files/VirtualFileType"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"

export type FileBound = {
  readonly size: Bytes
  readonly modified: Time
  readonly stream: AsyncSequence<BlobLike>
}

const dispatch = FullMethodDispatcher<
  [VirtualFile, PdfEngine, TrackingCallback],
  Promise<FileBound>
>({
  [VirtualFileType.REMOTE_FILE_CHUNK]: (
    file,
    engine,
    emit
  ): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () =>
      First(
        FilterAsync(
          f => f.bytes_start === file.bytes_start,
          engine.DownloadFile(file.remote, { cache_evict: true })
        )
      )
    ),
  [VirtualFileType.REMOTE_FILE]: async (file, engine, emit) => {
    const { value, stream, error } = await Peek(engine.DownloadFile(file))

    await AutoThrow(error)

    const generator = async function* () {
      for await (const chunk of stream) {
        const { stream } = await RequestBlob(engine, chunk, emit)
        yield* stream
      }
    }

    return {
      size: value!.bytes_total,
      modified: UnixTime(),
      stream: generator(),
    }
  },

  [VirtualFileType.GENERATED_FILE]: async (file, engine, _emit) => {
    const blob = await FileCache(engine.config.file_cache).get(
      FileContentGUID(file)
    )

    if (!blob) {
      console.error("Cache miss for file", file)
      throw EngineError(ErrorName.CACHE_EXPIRED)
    }

    return {
      stream: [blob],
      size: blob.size as Bytes,
      modified: file.modified,
    }
  },

  [VirtualFileType.SINGLE_PAGE_IMAGE]: (file, engine, emit) =>
    dispatchWithFallback(file.content, engine, emit, () =>
      First(engine.RasterizePdf(file.source, { cache_evict: true }))
    ),

  [VirtualFileType.SINGLE_PAGE_PDF]: (file, engine, emit): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () => {
      if (file.source) {
        return First(
          FilterAsync(
            f => f.page_number === file.page_number,
            engine.SplitPdf(file.source, { cache_evict: true })
          )
        )
      }
      return engine.PlotPdf(file.page, file.context, {
        task_id: FileContentGUID(file),
        cache_evict: true,
      })
    }),

  [VirtualFileType.MULTI_PAGE_PDF]: (file, engine, emit): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () =>
      engine.MergePdf(file.pages, {
        task_id: FileContentGUID(file),
        cache_evict: true,
      })
    ),

  [VirtualFileType.ZIP_FILE]: (file, engine, emit): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () =>
      engine.ZipFiles(Items(file.files), {
        task_id: FileContentGUID(file),
        cache_evict: true,
      })
    ),
})

const dispatchWithFallback = async (
  file: GeneratedFile | RemoteFile,
  engine: PdfEngine,
  emit: TrackingCallback = Void,
  fallback: () => Promise<VirtualFile>
): Promise<FileBound> => {
  const { value, error } = await SafePromise(dispatch(file, engine, emit))

  if (error) {
    const computed = await fallback()

    if (FileContentGUID(computed) === FileContentGUID(file)) {
      return dispatch(computed, engine, emit)
    }

    if (FileContentSize(computed) === FileContentSize(file)) {
      console.error("⚠️ sha256 failed, size passed", { computed, file })

      return dispatch(computed, engine, emit)
    }

    throw EngineError(ErrorName.CACHE_MISMATCH)
  }

  return value
}

export const RequestBlob = (
  engine: PdfEngine,
  file: VirtualFile,
  emit: TrackingCallback = Void
): Promise<FileBound> => dispatch(file, engine, emit)
